import React from 'react';
import { Link } from 'lucide-react';

interface InstructorViewProps {
  values: any;
  instructors: Array<{ value: number; label: string }>;
  serviceOptions: Array<{ value: number; label: string }>;
  hotels: Array<{
    value: string | number;
    label: string;
    google_map_link?: string;
  }>;
  cars: Array<{ value: string | number; label: string }>;
  mountains: Array<{ value: string | number; label: string }>;
  agents: Array<{ value: string | number; label: string }>;
  lessonsEntry?: string;
  isMobile?: boolean;
}

const InstructorView: React.FC<InstructorViewProps> = ({
  values,
  instructors,
  serviceOptions,
  hotels,
  cars,
  mountains,
  agents,
  lessonsEntry
}) => {
  const getLabel = (options: any[], value: any) => {
    const found = options.find(opt => String(opt.value) === String(value));
    return found ? found.label : value || '-';
  };

  const selectedHotel = hotels.find(
    h => String(h.value) === String(values.hotel_id)
  );

  return (
    <div className='space-y-4'>
      {/* Lesson details table wrap with overflow-x-auto for responsiveness */}
      <div className='overflow-x-auto'>
        <table
          className='table-fixed w-full border border-gray-300 rounded-xl text-sm text-center mx-auto mb-4'
          style={{ minWidth: '1250px' }}
        >
          <colgroup>
            <col style={{ width: '200px' }} />
            <col style={{ width: '80px' }} />
            <col style={{ width: '200px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '80px' }} />
            <col style={{ width: '80px' }} />
            <col style={{ width: '80px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '140px' }} />
            <col style={{ width: '140px' }} />
          </colgroup>
          <thead className='bg-blue-100 text-gray-800'>
            <tr>
              <th className='py-2 px-2.5 border'>Instructor</th>
              <th className='py-2 px-2.5 border'>String</th>
              <th className='py-2 px-2.5 border'>Client</th>
              <th className='py-2 px-2.5 border'>Service</th>
              <th className='py-2 px-2.5 border'>AM</th>
              <th className='py-2 px-2.5 border'>PM</th>
              <th className='py-2 px-2.5 border'>FD</th>
              <th className='py-2 px-2.5 border'>Level</th>
              <th className='py-2 px-2.5 border'>Start</th>
              <th className='py-2 px-2.5 border'>End</th>
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            <tr>
              <td className='border px-2 py-3 break-words whitespace-normal'>
                {getLabel(instructors, values.instructor_id)}
              </td>
              <td className='border px-2 py-3 break-words whitespace-normal'>
                {values.string || '-'}
              </td>
              <td className='border px-2 py-3 font-medium break-words whitespace-normal'>
                {values.client_name || '-'}
              </td>
              <td className='border px-2 py-3 break-words whitespace-normal'>
                {getLabel(serviceOptions, values.lesson_code_id)}
              </td>
              <td className='border px-2 py-3'>{values.am ?? '0'}</td>
              <td className='border px-2 py-3'>{values.pm ?? '0'}</td>
              <td className='border px-2 py-3'>{values.fd ?? '0'}</td>
              <td className='border px-2 py-3 break-words whitespace-normal'>
                {values.ski_level || '-'}
              </td>
              <td className='border px-2 py-3 font-semibold text-blue-700'>
                {values.start || '-'}
              </td>
              <td className='border px-2 py-3'>{values.end || '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Logistics & Timing Section */}
      <div className='grid grid-cols-1 gap-4'>
        <div className='overflow-x-auto'>
          <table
            className='table-fixed w-full border border-gray-300 rounded-xl text-sm text-center mx-auto'
            style={{ minWidth: '800px' }}
          >
            <thead className='bg-blue-100 text-gray-800'>
              <tr>
                <th className='min-w-[160px] py-2 px-2.5 border'>Hotel</th>
                <th className='min-w-[140px] py-2 px-2.5 border'>Vehicle</th>
                <th className='min-w-[120px] py-2 px-2.5 border'>
                  Pick-up numbers
                </th>
                <th className='min-w-[160px] py-2 px-2.5 border'>
                  Pickup Time
                </th>
                <th className='min-w-[160px] py-2 px-2.5 border'>Mountain</th>
              </tr>
            </thead>
            <tbody className='bg-white'>
              <tr>
                <td className='border px-2 py-3'>
                  <div className='font-medium text-gray-900'>
                    {getLabel(hotels, values.hotel_id)}
                  </div>
                  {selectedHotel?.google_map_link && (
                    <a
                      href={selectedHotel.google_map_link}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-blue-600 underline flex items-center justify-center gap-1 mt-1 text-xs'
                    >
                      Google Map Link
                      <Link size={14} />
                    </a>
                  )}
                </td>
                <td className='border px-2 py-3 text-slate-700'>
                  {getLabel(cars, values.car_id)}
                </td>
                <td className='border px-2 py-3 text-gray-600'>
                  {values.transport_pax ?? '0'}
                </td>
                <td className='border px-2 py-3 font-semibold text-gray-900'>
                  {values.pickup_time || '-'}
                </td>
                <td className='border px-2 py-3 text-slate-700'>
                  {getLabel(mountains, values.mountain_id)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className='overflow-x-auto'>
          <table
            className='table-fixed w-full border border-gray-300 rounded-xl text-sm text-center mx-auto'
            style={{ minWidth: '500px' }}
          >
            <thead className='bg-blue-100 text-gray-800'>
              <tr>
                <th className='min-w-[160px] py-2 px-2.5 border'>Agent</th>
                <th className='min-w-[150px] py-2 px-2.5 border'>Ref</th>
                <th className='w-auto py-2 px-2.5 border'>Pro/Unpro</th>
              </tr>
            </thead>
            <tbody className='bg-white'>
              <tr>
                <td className='border px-2 py-3 text-slate-700'>
                  {getLabel(agents, values.agent_id)}
                </td>
                <td className='border px-2 py-3 text-gray-600'>
                  {values.reference || '-'}
                </td>
                <td className='border px-2 py-3'>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                      values.paid === 'Y' || lessonsEntry === 'Y'
                        ? 'bg-green-100 text-green-800 border border-green-200'
                        : 'bg-gray-100 text-gray-800 border border-gray-200'
                    }`}
                  >
                    {values.paid === 'Y' || lessonsEntry === 'Y'
                      ? 'PRO'
                      : 'UNPRO'}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Comments Section */}
      <div className='w-full'>
        <table className='table-fixed w-full border border-gray-300 rounded-xl text-sm mx-auto'>
          <thead className='bg-blue-100 text-gray-800'>
            <tr>
              <th className='py-2 px-2.5 border'>Important Comments</th>
            </tr>
          </thead>
          <tbody className='bg-white'>
            <tr>
              <td className='border px-4 py-4 text-left whitespace-pre-wrap min-h-[60px] text-slate-700'>
                {values.comments || (
                  <span className='text-gray-400 italic'>
                    No special comments provided for this lesson.
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InstructorView;
